import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../../api/api";
import { APIError } from "../../../constants/types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { showMessage } from "../../messages/messagesActions";
import { INITIAL_RISK_PROFILE_V2, IRiskProfileV2 } from "./commAutoRiskProfileV2.types";
import { buildBodyKey, buildListKey } from "../leadKeying";

export const fetchRiskProfileV2ListAsync = createAsyncThunk<
  { data: IRiskProfileV2[] },
  { customerPolicyId?: number; leadId?: string | null; status: string },
  { rejectValue: APIError }
>(
  "riskProfileV2/fetchList",
  async ({ customerPolicyId, leadId, status }, { rejectWithValue }) => {
    try {
      // Lead scope keys by `lead_id` (omitting `customer_policy_id`); otherwise the
      // legacy `customer_policy_id` query is emitted byte-for-byte via the shared helper.
      let url = `/policy/get-policy-commauto-risk-profile-v2?${buildListKey({ customerPolicyId, leadId })}`;
      if (status !== "-1") {
        url += `&status=${status}`;
      }
      const res = await api.get(url);
      const data: IRiskProfileV2[] = res.data.data;
      return { data };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchRiskProfileV2Async = createAsyncThunk<
  { data: IRiskProfileV2 },
  { customerPolicyId?: number; leadId?: string | null; incrementalNumber: number },
  { rejectValue: APIError }
>(
  "riskProfileV2/fetch",
  async ({ customerPolicyId, leadId, incrementalNumber }, { rejectWithValue }) => {
    try {
      // `incremental_number === n` is preserved in both scopes so fetch-one keeps its
      // identifier; only the scope key (`lead_id` vs legacy `customer_policy_id`) varies.
      const res = await api.get(
        `/policy/get-policy-commauto-risk-profile-v2?${buildListKey({ customerPolicyId, leadId })}&incremental_number=${incrementalNumber}`,
      );
      const data: IRiskProfileV2[] = res.data.data;
      return { data: data.length > 0 ? data[0] : INITIAL_RISK_PROFILE_V2 };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const saveRiskProfileV2Async = createAsyncThunk<
  { data: IRiskProfileV2 },
  {
    data: IRiskProfileV2;
    leadId?: string | null;
    onCallback: (isSuccess: boolean, data?: IRiskProfileV2) => void;
  },
  { rejectValue: APIError }
>(
  "riskProfileV2/save",
  async ({ data, leadId, onCallback }, { rejectWithValue, dispatch }) => {
    try {
      // Lead scope re-keys the body to `{ ...data, lead_id, customer_policy_id: null }`
      // (preserving `incremental_number`); otherwise the body is sent verbatim (`{ ...data }`).
      const res = await api.post(
        "/policy/edit-policy-commauto-risk-profile-v2",
        buildBodyKey({ ...data }, leadId),
      );
      onCallback(true, res.data.data);
      dispatch(
        showMessage({
          type: "success",
          message: "Risk Profile saved successfully!",
          displayAs: "snackbar",
        }),
      );
      return { data: res.data.data };
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
          displayAs: "snackbar",
        }),
      );
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);
